import {
  CaretRight,
  Gift,
  LightbulbFilament,
  PhoneCall,
  Television,
  Ticket,
  WifiHigh,
} from "@phosphor-icons/react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useReferralRewards } from "../../../hooks/useReferralRewards";

const billTypes = [
  {
    name: "Data Bundles",
    icon: WifiHigh,
    id: "data",
    available: true,
    note: "Available now",
  },
  {
    name: "Airtime",
    icon: PhoneCall,
    id: "airtime",
    available: true,
    note: "Available now",
  },
  {
    name: "Cable TV",
    icon: Television,
    id: "cable-tv",
    available: true,
    note: "Available now",
  },
  {
    name: "Electricity",
    icon: LightbulbFilament,
    id: "electricity",
    available: true,
    note: "Available now",
  },
  {
    name: "E-Pin",
    icon: Ticket,
    id: "e-pin",
    available: false,
    note: "Coming soon",
  },
];

const SelectBillType = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: rewardsData } = useReferralRewards({ status: "CLAIMABLE", take: 1 });
  const claimableCount = rewardsData?.data?.total ?? 0;

  const handleNext = useCallback(
    (id: string, available: boolean) => {
      if (!available) {
        return;
      }

      if (!user?.pin_status) {
        navigate(`/pin/setup?redirect=/payments/bills/${id}`);
      } else {
        navigate(`/payments/bills/${id}`);
      }
    },
    [user, navigate]
  );

  return (
    <div>
      <div className="flex items-center justify-center gap-10 py-10 text-center flex-col">
        <div className="flex flex-col gap-2 px-5">
          <div className="font-bold text-3xl text-gray-800">
            What type of bill do you want to pay?
          </div>
          <div className="text-gray-500">
            Data, airtime, cable TV and electricity are available now. E-Pin is
            still being prepared and will be opened soon.
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 items-stretch justify-center gap-2">
          {billTypes.map((billType, key) => {
            return (
              <button
                disabled={!billType.available}
                onClick={() => handleNext(billType.id, billType.available)}
                key={key}
                className={`p-10 px-4 lg:px-8 flex items-center justify-center flex-col gap-4 w-40 aspect-square border rounded-md border-gray-200 transition ${
                  billType.available
                    ? "hover:bg-gray-50"
                    : "opacity-45 grayscale cursor-not-allowed bg-gray-50"
                }`}
              >
                <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10">
                  <billType.icon weight="bold" size={18} />
                </div>
                <div className="font-medium">{billType.name}</div>
                <div
                  className={`text-xs rounded-full px-2 py-1 ${
                    billType.available
                      ? "bg-green-50 text-green-600"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {billType.note}
                </div>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => navigate("/payments/bills/referral-rewards")}
          className="w-full max-w-md mx-auto flex items-center gap-4 rounded-2xl border border-visaro-orange/30 bg-visaro-orange/5 p-4 text-left hover:bg-visaro-orange/10 transition"
        >
          <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10 shrink-0">
            <Gift weight="duotone" size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-deepBlue">Referral rewards</div>
            <div className="text-sm text-gray-500">
              {claimableCount > 0
                ? `You have ${claimableCount} reward${
                    claimableCount === 1 ? "" : "s"
                  } to claim`
                : "Claim data & airtime gifts from referrals"}
            </div>
          </div>
          {claimableCount > 0 && (
            <span className="shrink-0 min-w-6 h-6 px-2 flex items-center justify-center rounded-full bg-visaro-orange text-white text-xs font-bold">
              {claimableCount}
            </span>
          )}
          <CaretRight className="text-gray-400 shrink-0" />
        </button>
      </div>
    </div>
  );
};

export default SelectBillType;
